"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Badge, Button, Field, Label, Skeleton } from "@/components/ui";
import { RHFInput } from "@/components/ui/hook-form";
import {
  getPasswordSecondFactorStatus,
  SecondFactorApiError,
  updatePasswordSecondFactor,
} from "@/lib/auth-password-second-factor";
import { cn } from "@/lib/utils";
import {
  makePasswordSecondFactorSettingsSchema,
  type PasswordSecondFactorSettingsValues,
} from "@/lib/validations/auth";

const QUERY_KEY = ["auth", "password-second-factor"];

export function SecondFactorPasswordPanel() {
  const t = useTranslations("profile.security");
  const vt = useTranslations("validation");
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getPasswordSecondFactorStatus,
  });

  const enabled = !!status.data?.enabled;

  const schema = useMemo(
    () => makePasswordSecondFactorSettingsSchema(vt, enabled),
    [vt, enabled],
  );

  const form = useForm<PasswordSecondFactorSettingsValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const updatePassword = useMutation({
    mutationFn: updatePasswordSecondFactor,
    onSuccess: (data) => {
      // The PUT already returns the fresh status — seed the cache directly
      // instead of forcing a redundant GET round-trip.
      queryClient.setQueryData(QUERY_KEY, data);
      form.reset();
      toast.success(t("success"));
    },
    onError: (error) => {
      // Only a genuinely wrong/missing current password should flag the field.
      // Network failures, 500s and expired sessions must NOT read as
      // "current password is incorrect".
      const code = error instanceof SecondFactorApiError ? error.code : null;
      if (
        code === "invalid_current_password" ||
        code === "current_password_required"
      ) {
        form.setError("current_password", {
          type: "server",
          message: t("invalidCurrent"),
        });
        return;
      }
      toast.error(t("error"));
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await updatePassword.mutateAsync({
        current_password: enabled ? values.current_password : undefined,
        new_password: values.new_password,
      });
    } catch {
      // Surfaced via the mutation's onError above; caught here so the rejected
      // promise doesn't escape react-hook-form's handleSubmit.
    }
  });

  if (status.isLoading) {
    return (
      <div className="space-y-6 py-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-6 w-16 rounded-xs" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  if (status.isError) {
    return (
      <div className="space-y-4 py-2">
        <p className="text-sm text-text-secondary">{t("loadError")}</p>
        <Button
          type="button"
          variant="bordered"
          colors="secondary"
          className="mr-auto"
          loading={status.isFetching}
          disabled={status.isFetching}
          onClick={() => status.refetch()}
        >
          {t("retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-text-primary">
          {t("title")}
        </h2>
        <Badge
          variant="outline"
          className={cn(
            enabled
              ? "border-semantic-success/40 text-semantic-success"
              : "text-text-secondary",
          )}
        >
          {enabled ? t("enabled") : t("disabled")}
        </Badge>
      </div>

      <FormProvider {...form}>
        <form onSubmit={onSubmit} className="space-y-4">
          {enabled ? (
            <Field>
              <Label htmlFor="current-second-factor-password">
                <span>{t("currentPassword")}</span>
              </Label>
              <RHFInput
                id="current-second-factor-password"
                name="current_password"
                type="password"
                dir="ltr"
                autoComplete="current-password"
                disabled={updatePassword.isPending}
                onChange={() => form.clearErrors("current_password")}
              />
            </Field>
          ) : null}

          <Field>
            <Label htmlFor="new-second-factor-password">
              <span>{t("newPassword")}</span>
            </Label>
            <RHFInput
              id="new-second-factor-password"
              name="new_password"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              disabled={updatePassword.isPending}
            />
          </Field>

          <Field>
            <Label htmlFor="confirm-second-factor-password">
              <span>{t("confirmPassword")}</span>
            </Label>
            <RHFInput
              id="confirm-second-factor-password"
              name="confirm_password"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              disabled={updatePassword.isPending}
            />
          </Field>

          <Button
            type="submit"
            variant="solid"
            colors="secondary"
            className="mr-auto"
            loading={updatePassword.isPending}
            disabled={updatePassword.isPending}
          >
            {enabled ? t("updateButton") : t("setButton")}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}
